// member-portal/src/components/InvoiceEditor.js — FINAL (fixed loading stuck)

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import './InvoiceEditor.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const InvoiceEditor = ({ invoiceId, onClose }) => {
  const [invoice, setInvoice] = useState(null);
  const [credits, setCredits] = useState([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice();
    }
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('memberToken');
      const res = await axios.get(`${API_BASE}/api/superadmin/billing/invoice/${invoiceId}/review`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        params: { t: Date.now() }
      });

      const data = res.data.invoice;
      setInvoice(data);
      setCredits(data.credits || []);
      setNotes(data.notes || '');
      setStatus(data.status || 'draft');
      setVersion(prev => prev + 1);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError('Failed to load invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addCredit = () => {
    setCredits([...credits, { description: '', amount: 0 }]);
  };

  const updateCredit = (index, field, value) => {
    const updated = [...credits];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setCredits(updated);
  };

  const removeCredit = (index) => {
    setCredits(credits.filter((_, i) => i !== index));
  };

  const saveInvoice = async (newStatus = status) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('memberToken');
      await axios.patch(`${API_BASE}/api/superadmin/billing/invoice/${invoiceId}`, {
        credits,
        notes,
        status: newStatus,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(newStatus === 'sent' ? 'Invoice sent!' : 'Invoice saved!');
      if (newStatus === 'sent') {
        onClose();
      } else {
        fetchInvoice();
      }
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading invoice…</div>;
  if (error) return <div className="error">{error}</div>;
  if (!invoice) return <div className="error">Invoice not found</div>;

  const totalCredits = credits.reduce((sum, c) => sum + c.amount, 0);
  const finalTotal = Math.max(0, invoice.charges.grandTotal - totalCredits);

  return (
    <div className="invoice-editor">
      <div className="header">
        <h1>Invoice {invoice.invoiceNumber}</h1>
        <div className="actions">
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
      </div>

      <div className="info-grid">
        <div>
          <strong>{invoice.org.companyName}</strong><br />
          {invoice.org.accountNumber}<br />
          {invoice.org.vatNumber}<br />
          {invoice.org.address}
        </div>
        <div className="text-right">
          <strong>Period:</strong> {format(new Date(invoice.period), 'MMMM yyyy')}<br />
          <strong>Status:</strong> <span className={`status ${status}`}>{status.toUpperCase()}</span>
        </div>
      </div>

      <table className="charges-table">
        <thead>
          <tr>
            <th>Description</th>
            <th className="text-right">Quantity</th>
            <th className="text-right">Rate</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Deal Impressions</td>
            <td className="text-right">{invoice.stats.impressions.toLocaleString()}</td>
            <td className="text-right">R0.05</td> {/* ← changed */}
            <td className="text-right">R{(invoice.stats.impressions * 0.05).toFixed(2)}</td>
          </tr>
          <tr>
            <td>Deal Views</td>
            <td className="text-right">{invoice.stats.views.toLocaleString()}</td>
            <td className="text-right">R0.10</td> {/* ← changed */}
            <td className="text-right">R{(invoice.stats.views * 0.10).toFixed(2)}</td>
          </tr>
          <tr>
            <td>Deal Clicks / Redirects</td>
            <td className="text-right">{invoice.stats.clicks.toLocaleString()}</td>
            <td className="text-right">R1.00</td>
            <td className="text-right">R{(invoice.stats.clicks * 1.0).toFixed(2)}</td>
          </tr>
          <tr className="total">
            <td colSpan="3">Total Excl. VAT</td>
            <td className="text-right">R{invoice.charges.totalExclVat.toFixed(2)}</td>
          </tr>
          <tr>
            <td colSpan="3">VAT @ 15%</td>
            <td className="text-right">R{invoice.charges.vat.toFixed(2)}</td>
          </tr>
          <tr className="grand-total">
            <td colSpan="3">Grand Total</td>
            <td className="text-right">R{invoice.charges.grandTotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className="credits-section">
        <h3>Credits / Adjustments</h3>
        {credits.map((credit, i) => (
          <div key={i} className="credit-row">
            <input
              value={credit.description}
              onChange={(e) => updateCredit(i, 'description', e.target.value)}
              placeholder="Reason (e.g. Goodwill, Error)"
            />
            <input
              type="number"
              value={credit.amount}
              onChange={(e) => updateCredit(i, 'amount', e.target.value)}
              placeholder="Amount"
              step="0.01"
            />
            <button onClick={() => removeCredit(i)} className="remove-btn">×</button>
          </div>
        ))}
        <button onClick={addCredit} className="add-credit-btn">+ Add Credit</button>
        {totalCredits > 0 && (
          <div className="credit-total">
            Total Credits: <strong>-R{totalCredits.toFixed(2)}</strong>
          </div>
        )}
      </div>

      <div className="notes-section">
        <h3>Internal Notes</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any internal notes..."
          rows="3"
        />
      </div>

      <div className="final-amount">
        <strong>Final Amount Due: R{finalTotal.toFixed(2)}</strong>
      </div>

      <div className="action-buttons">
        <button onClick={() => saveInvoice('draft')} disabled={saving}>
          Save Draft
        </button>
        <button onClick={() => saveInvoice('approved')} disabled={saving} className="approve-btn">
          Approve
        </button>
        <button onClick={() => saveInvoice('sent')} disabled={saving} className="send-btn">
          Approve & Send
        </button>
      </div>

      {invoice.filePath && (
        <div className="preview-section">
          <h3>Current PDF</h3>
          <iframe 
            key={version} 
            src={`${invoice.filePath}?v=${Date.now()}`} 
            width="100%" 
            height="800" 
            title="Invoice PDF" 
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceEditor;